"use client";

import React from "react";
import { Mail, MapPin, Phone, ArrowRight, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const contactDetails = [{
  icon: Mail,
  title: "Στείλτε μας Email",
  value: "info@milconsult.gr",
  link: "mailto:info@milconsult.gr"
}, {
  icon: Phone,
  title: "Καλέστε μας",
  value: "+30 210 123 4567",
  link: "tel:+302101234567"
}, {
  icon: MapPin,
  title: "Τοποθεσία",
  value: "Αθήνα, Ελλάδα",
  link: "#"
}];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="194641" className="flex flex-col items-center justify-center h-full min-h-[400px] p-8 text-center bg-primary/10 rounded-sm border border-primary/20 animate-in fade-in duration-500">
        <div className="w-16 h-16 bg-primary/20 rounded-sm flex items-center justify-center mb-6">
          <Mail className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-2xl font-serif text-foreground mb-2">Το μήνυμα ελήφθη</h3>
        <p className="text-muted-foreground mb-8 max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-sm px-8 border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300">
          Αποστολή νέου μηνύματος
        </Button>
      </div>;
  }
  const inputStyles = "border-x-0 border-t-0 border-b-2 border-border/50 rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-primary bg-transparent text-foreground placeholder:text-muted-foreground/30 transition-colors duration-300";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="space-y-8">
        <FormField control={control} name="name" rules={{
        required: "Παρακαλώ εισάγετε το όνομά σας",
        minLength: {
          value: 2,
          message: "Το όνομα πρέπει να έχει τουλάχιστον 2 χαρακτήρες"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-widest text-primary font-medium">Ονοματεπώνυμο</FormLabel>
              <FormControl>
                <Input placeholder="Εισάγετε το όνομά σας" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Παρακαλώ εισάγετε το email σας",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Παρακαλώ εισάγετε μια έγκυρη διεύθυνση email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-widest text-primary font-medium">Διεύθυνση Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="example@milconsult.gr" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Παρακαλώ εισάγετε το μήνυμά σας",
        minLength: {
          value: 10,
          message: "Το μήνυμα πρέπει να έχει τουλάχιστον 10 χαρακτήρες"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-widest text-primary font-medium">Το Μήνυμά σας</FormLabel>
              <FormControl>
                <Textarea placeholder="Πώς μπορούμε να σας βοηθήσουμε;" className={`min-h-[120px] resize-none ${inputStyles}`} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-sm text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto rounded-sm px-10 h-14 group bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 font-medium tracking-wide">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Αποστολή...
          </> : <>
            Αποστολή Μηνύματος
            <ArrowRight className="ml-2 size-5 transition-transform group-hover:translate-x-1" />
          </>}
      </Button>
    </Form>;
}
export default function ContactUs() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: detailsRef,
    isVisible: isDetailsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="contact" className="py-24 lg:py-32 bg-background relative overflow-hidden text-foreground">
      <div className="absolute top-0 right-0 w-1/3 h-full bg-primary/10 -skew-x-12 translate-x-1/2 opacity-30 pointer-events-none" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-12 gap-16 lg:gap-24">
          
          <div className="lg:col-span-5 flex flex-col justify-center">
            <div ref={headerRef} className={`transition-all duration-700 ease-out ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <h2 className="text-4xl md:text-5xl font-serif text-foreground mb-6 leading-tight">
                Ας συζητήσουμε το επόμενο βήμα σας.
              </h2>
              <p className="text-lg text-muted-foreground mb-12 max-w-md leading-relaxed">
                Είμαστε πάντα ανοιχτοί σε νέες προκλήσεις και έτοιμοι να προσφέρουμε τις εξειδικευμένες συμβουλευτικές μας υπηρεσίες για την επίτευξη των στόχων σας.
              </p>
            </div>

            
          </div>

          <div ref={formRef} className={`lg:col-span-7 transition-all duration-700 delay-300 ease-out ${isFormVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="bg-card p-8 md:p-12 rounded-sm shadow-xl shadow-black/20 border border-primary/10">
              <FormProvider formKey="contact_form_home_alt" formId="contact_form_home_alt" sectionName="ContactUs" defaultValues={{
              name: "",
              email: "",
              message: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              },
              message: {
                required: true,
                minLength: 10
              }
            }} submitText="Αποστολή Μηνύματος" successMessage="Σας ευχαριστούμε για την επικοινωνία. Θα επικοινωνήσουμε μαζί σας σύντομα.">
                <ContactFormInner />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}